/**
 * Shared signed-URL cache for chat (and similar) media.
 * Backend signed URLs expire after ~1 hour; we refresh before then.
 *
 * Chat keeps durable storage keys in Matrix and resolves signed URLs at render time.
 * Returning after a long background (or staying open past expiry) refreshes in place —
 * no reliance on a full page reload for messaging media.
 */

import { getSignedUrls } from './fileOperations';

/** Match SIGNED_URL_REFRESH.RELOAD_AFTER_MS — refresh before typical 1h backend expiry. */
export const SIGNED_URL_TTL_MS = 55 * 60 * 1000;
export const SIGNED_URL_REFRESH_EVENT = 'jh:refresh-signed-urls';

const cache = new Map();
const pendingRequests = new Map();

/**
 * Convert a storage key or signed URL into a durable object key for re-signing.
 * Signed URLs must never be persisted in Matrix — only durable keys.
 * @param {unknown} value
 * @returns {string|null}
 */
export function toDurableFileKey(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  try {
    const url = new URL(trimmed);
    const params = url.searchParams;
    const looksSigned = [...params.keys()].some(
      (key) =>
        /^X-Amz-/i.test(key) ||
        key === 'Signature' ||
        key === 'Expires' ||
        key === 'AWSAccessKeyId'
    );

    // Public permanent URLs are left as-is by callers; only signed URLs become keys.
    if (!looksSigned) return null;

    const path = decodeURIComponent(url.pathname || '').replace(/^\/+/, '');
    return path || null;
  } catch {
    return null;
  }
}

/**
 * True when value is a non-HTTP storage key that needs signing.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isSignableFileKey(value) {
  return typeof value === 'string' && value.trim() !== '' && !/^https?:\/\//i.test(value.trim());
}

/**
 * Normalize content assets so Matrix / clients only store durable keys.
 * Signed URLs are converted to object keys; plain keys and public URLs are kept.
 * @param {unknown} assets
 * @returns {Array<object>}
 */
export function normalizeAssetsForMatrix(assets) {
  if (!Array.isArray(assets)) return [];

  return assets
    .map((asset) => {
      if (!asset || typeof asset !== 'object') return null;

      const key = resolveAssetKeyForMatrix(asset.key);
      if (!key) return null;

      const next = {
        ...asset,
        key,
      };

      if (asset.poster != null && asset.poster !== '') {
        const posterKey = resolveAssetKeyForMatrix(asset.poster);
        if (posterKey) next.poster = posterKey;
        else delete next.poster;
      }

      return next;
    })
    .filter(Boolean);
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
function resolveAssetKeyForMatrix(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  const trimmed = value.trim();

  // Prefer durable key extracted from a signed URL.
  const fromSigned = toDurableFileKey(trimmed);
  if (fromSigned && isSignableFileKey(fromSigned)) return fromSigned;

  if (isSignableFileKey(trimmed)) return trimmed;

  // Non-signed http(s) URL (public/CDN without signature) — keep as-is.
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  return null;
}

export function getCachedSignedUrl(key) {
  const cached = cache.get(key);
  if (cached && cached.expires > Date.now()) return cached.url;
  return undefined;
}

export function setCachedSignedUrl(key, url) {
  cache.set(key, { url, expires: Date.now() + SIGNED_URL_TTL_MS });
}

export function invalidateCachedSignedUrl(key) {
  if (!key) return;
  cache.delete(key);
  pendingRequests.delete(key);
}

export function invalidateCachedSignedUrls(keys = []) {
  keys.forEach(invalidateCachedSignedUrl);
}

/**
 * Resolve signed URLs for durable keys. Dedupes in-flight requests and uses TTL cache.
 * @param {string[]} keys
 * @param {{ force?: boolean, isProfile?: boolean }} [options]
 * @returns {Promise<Record<string, string>>}
 */
export async function resolveSignedUrlMap(keys, options = {}) {
  const { force = false, isProfile = false } = options;
  const uniqueKeys = [...new Set((keys || []).filter(isSignableFileKey))];
  const result = {};

  if (!uniqueKeys.length) return result;

  if (force) {
    invalidateCachedSignedUrls(uniqueKeys);
  }

  const keysToFetch = [];
  const waitFor = [];

  uniqueKeys.forEach((key) => {
    const cached = getCachedSignedUrl(key);
    if (cached) {
      result[key] = cached;
      return;
    }

    const existing = pendingRequests.get(key);
    if (existing) {
      waitFor.push(existing);
      return;
    }

    keysToFetch.push(key);
  });

  if (keysToFetch.length) {
    const fetchPromise = (async () => {
      try {
        const data = await getSignedUrls(keysToFetch, isProfile);
        if (!Array.isArray(data)) return;

        data.forEach((item) => {
          if (item?.key && item?.signedUrl) {
            setCachedSignedUrl(item.key, item.signedUrl);
          }
        });
      } finally {
        keysToFetch.forEach((key) => pendingRequests.delete(key));
      }
    })();

    keysToFetch.forEach((key) => pendingRequests.set(key, fetchPromise));
    waitFor.push(fetchPromise);
  }

  if (waitFor.length) {
    await Promise.all(waitFor);
  }

  uniqueKeys.forEach((key) => {
    const url = getCachedSignedUrl(key);
    if (url) result[key] = url;
  });

  return result;
}

/**
 * Notify listeners (e.g. chat media) to refresh signed URLs without a full reload.
 */
export function emitSignedUrlRefresh() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SIGNED_URL_REFRESH_EVENT));
}
